import Backbone from 'backbone'
import _ from 'lodash'
import template from './base-percentages-on-panel.hbs'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import formHelper from 'scoreboard/components/form-helper'
import highlight from 'scoreboard/components/highlight'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import i18n from 'scoreboard/components/i18n'
import unwrapModel from 'scoreboard/components/unwrap-model'

const BasePercentagesOnPanel = Backbone.View.extend({

	/**
	 * The panel containing all the controls to edit the "Percentage Of"
	 * settings for a dataset series or a column in a dataset table.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model
	 *
	 * @param {Object} options.aggregation the aggregation (aka column) being edited
	 *
	 * @param {FormView} options.formView the formView this panel participates in
	 *
	 * @param {DatasetTable|Object} options.table the dataset table the aggregation belongs to
	 *
	 * @param {Boolean} [options.showPercentageOf=true] indicates that the
	 * "percentageOf" radio buttons should be shown
	 *
	 * @param {Function} options.numeratorFiltersRetriever function that returns
	 * the numerator filters. Used so that when the "useColumnFilters" option
	 * is toggled, we can copy the numerator filters to the denominator
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = this.options.formView
		this.table = unwrapModel(this.options.table)
		this.denominatorFilters = _.cloneDeep(_.get(this.options.aggregation, 'denominatorFilters', []))
	},

	className: 'base-percentages-on panel',

	events: {
		'click .add-filter': 'addFilter',
		'change [name=percentageOf]': 'onChangePercentageOf'
	},

	/** @type {Dataset} */
	model: null,

	render: function() {
		this.$el.empty().append(template(this.context()))

		formHelper.writeToDom(this.options.aggregation, this.el)

		this.prepPercentageOf()
		this.renderUseColumnFiltersToggle()
		this.renderFilterBadges()
		this.onChangePercentageOf()

		return this
	},

	context: function() {
		if (this.table) {
			const { rowBreakdown, columnBreakdown } = this.table
			const value = this.model.getFieldLabelByIds(this.table)
			const row = !_.isNil(_.get(rowBreakdown, 'fieldId')) && this.model.getFieldLabelByIds(rowBreakdown)
			const column = !_.isNil(_.get(columnBreakdown, 'fieldId')) && this.model.getFieldLabelByIds(columnBreakdown)
			const valueAndRow = row && i18n.get('common.and2', highlight(value), highlight(row))
			const valueAndColumn = column && i18n.get('common.and2', highlight(value), highlight(column))
			const valueRowAndColumn = row && column && i18n.get('common.and2', highlight(value) + ', ' + highlight(row), highlight(column))
			return { value, row, column, valueAndRow, valueAndColumn, valueRowAndColumn }
		} else {
			return {}
		}
	},

	prepPercentageOf: function() {
		if (false == this.options.showPercentageOf) {
			this.$('.input-container label').remove()
			return
		}

		let percentageOf = this.options.aggregation?.percentageOf
		if (_.isNil(percentageOf)) {
			percentageOf = 'everything'
			let { rowBreakdown, columnBreakdown } = this.table ?? {}
			if (!_.isEmpty(rowBreakdown) && !_.isEmpty(columnBreakdown)) {
				percentageOf = 'value-and-column'
			} else if (!_.isEmpty(rowBreakdown)) {
				percentageOf = 'value'
			} else if (!_.isEmpty(columnBreakdown)) {
				percentageOf = 'column'
			}
		}

		this.$(`[name="percentageOf"][value="${percentageOf}"]`).prop('checked', true)
	},

	renderUseColumnFiltersToggle: function() {
		let checked = _.isNil(this.options.aggregation?.useColumnFilters) ? true
			: this.options.aggregation?.useColumnFilters
		let label = i18n.get('datasets.choose.percentageOf.useColumnFilters')
		if (this.options.aggregation?.scorecardItemId) {
			label = i18n.get('datasets.choose.percentageOf.useFilters')
		}
		this.toggle = new OnOffToggle({
			name: 'useColumnFilters',
			checked,
			label
		})
		this.$('.use-column-filters-toggle').append(this.toggle.el)
		this.toggle.render()
		this.toggle.on('change', (checked) => {
			let columnFiltersKey = 'datasets.choose.columns.columnFilters'
			if (!checked) {
				columnFiltersKey += '.clarified'
			}
			this.$('.filters.panel h3').text(i18n.get(columnFiltersKey))
			this.toggleDenominatorFilters()
		})
		this.toggle.change()
		// we don't want to trigger this the first time the form is rendered, so
		// we set it up as a separate event handler
		this.toggle.on('change', (checked) => {
			if (!checked) {
				this.denominatorFilters = _.cloneDeep(this.options.numeratorFiltersRetriever())
				this.denominatorFilterBadges.filters = this.denominatorFilters
				this.denominatorFilterBadges.render()
			}
		})
	},

	renderFilterBadges: function() {
		this.denominatorFilterBadges = new DatasetsFilterBadges({
			model: this.model,
			filters: this.denominatorFilters,
			treeItemsById: this.options.treeItemsById,
			noneMessage: i18n.get('common.thereAreNo', 'report.updateMetricsReport.filters'),
			allowAppCalendar: this.options.allowAppCalendar,
			showAggregationFilters: true
		})
		this.denominatorFilterBadges.on('add-filter edit-filter delete-filter',
			this.formView?.change?.bind?.(this.formView))
		this.$('.denominator-filters.panel-content').append(
			this.denominatorFilterBadges.el)
		this.denominatorFilterBadges.render()
	},

	addFilter: function() {
		this.denominatorFilterBadges.addFilter()
	},

	onChangePercentageOf: function() {
		let percentageOf = this.$('[name=percentageOf]:checked').attr('value')
		if (percentageOf === 'all-records') {
			// for all records, separate denominator filters don't make sense
			this.toggle.val(false)
		}
		this.toggleDenominatorFilters()
		this.$('.use-column-filters-toggle').toggleClass('is-not-displayed', percentageOf === 'all-records')
	},

	/**
	 * Shows or hides the denominator filters as appropriate based on the other
	 * form controls
	 */
	toggleDenominatorFilters: function() {
		let percentageOf = this.$('[name=percentageOf]:checked').attr('value')
		let showDenominatorFilters =
			// when the toggle is off we should show the denominator filters
			!this.toggle.val() &&
			// as long as percentageOf isn't 'all-records'
			percentageOf !== 'all-records'
		this.$('.denominator-filters').toggleClass('is-not-displayed', !showDenominatorFilters)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.denominatorFilterBadges, this.toggle], 'remove')
	}

})

export default BasePercentagesOnPanel

BasePercentagesOnPanel.__filename__ = 'datasets/base-percentages-on-panel.js'
